/**
 * Date Utilities for Checkout Application
 * Handles timezone conversion and consistent date/time formatting
 */

/**
 * Format a date string to the user's local timezone
 * @param {string} dateString - ISO date string from the backend
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted date/time string
 */
export const formatDate = (dateString, options = {}) => {
    if (!dateString) return options.fallback || 'Date not set';

    try {
        const date = new Date(dateString);

        // Check if date is valid
        if (isNaN(date.getTime())) {
            return 'Invalid date';
        }

        const {
            showDate = true,
            showTime = true,
            timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone, // Auto-detect user's timezone
            dateStyle = 'medium', // 'full', 'long', 'medium', 'short'
            timeStyle = 'short', // 'full', 'long', 'medium', 'short'
        } = options;

        // Build format options
        const formatOptions = {
            timeZone,
        };

        if (showDate && showTime) {
            formatOptions.dateStyle = dateStyle;
            formatOptions.timeStyle = timeStyle;
        } else if (showDate) {
            formatOptions.dateStyle = dateStyle;
        } else if (showTime) {
            formatOptions.timeStyle = timeStyle;
        }

        return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
    } catch (error) {
        console.error('Date formatting error:', error);
        return 'Invalid date';
    }
};

/**
 * Format a date to show only the date (no time)
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted date (e.g., "Dec 23, 2025")
 */
export const formatDateOnly = (dateString) => {
    return formatDate(dateString, {
        showDate: true,
        showTime: false,
        dateStyle: 'medium'
    });
};

/**
 * Format a date to show only the time (no date)
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted time (e.g., "2:30 PM")
 */
export const formatTimeOnly = (dateString) => {
    return formatDate(dateString, {
        showDate: false,
        showTime: true,
        timeStyle: 'short'
    });
};

/**
 * Format a date with full detail (long date and medium time)
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted datetime (e.g., "December 23, 2025, 2:30 PM")
 */
export const formatDateTimeFull = (dateString) => {
    return formatDate(dateString, {
        showDate: true,
        showTime: true,
        dateStyle: 'long',
        timeStyle: 'short'
    });
};

/**
 * Format a date in compact format (short date and time)
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted datetime (e.g., "12/23/25, 2:30 PM")
 */
export const formatDateTimeCompact = (dateString) => {
    return formatDate(dateString, {
        showDate: true,
        showTime: true,
        dateStyle: 'short',
        timeStyle: 'short'
    });
};

/**
 * Format relative time (e.g., "2 hours ago", "3 days ago")
 * @param {string} dateString - ISO date string
 * @returns {string} - Relative time string
 */
export const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Date not set';

    try {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHour = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHour / 24);

        if (diffSec < 60) {
            return 'just now';
        } else if (diffMin < 60) {
            return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
        } else if (diffHour < 24) {
            return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
        } else if (diffDay < 7) {
            return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
        } else {
            // For dates older than a week, show the actual date
            return formatDateOnly(dateString);
        }
    } catch (error) {
        console.error('Relative time formatting error:', error);
        return 'Invalid date';
    }
};

/**
 * Get user's timezone information
 * @returns {Object} - Timezone info including name and offset
 */
export const getUserTimezoneInfo = () => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = new Date();

    // Get timezone abbreviation
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'short'
    });

    const parts = formatter.formatToParts(now);
    const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value || '';

    return {
        timeZone,
        timeZoneName,
        offset: -now.getTimezoneOffset() / 60 // Offset in hours
    };
};

/**
 * Check if a date is today
 * @param {string} dateString - ISO date string
 * @returns {boolean}
 */
export const isToday = (dateString) => {
    if (!dateString) return false;

    try {
        const date = new Date(dateString);
        const today = new Date();

        return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
    } catch (error) {
        return false;
    }
};

/**
 * Check if a date is within the last N days
 * @param {string} dateString - ISO date string
 * @param {number} days - Number of days
 * @returns {boolean}
 */
export const isWithinLastDays = (dateString, days) => {
    if (!dateString) return false;

    try {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = diffMs / (1000 * 60 * 60 * 24);

        return diffDays >= 0 && diffDays <= days;
    } catch (error) {
        return false;
    }
};